import classNames from 'classnames';
import { equals } from 'ramda';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from "react-redux";
import { ReactComponent as PersonThinIcon } from '../../../../assets/svg/personThin.svg';
import StarIconOrange from "../../../../assets/svg/star_24_orange.svg";
import StarIconBlack from "../../../../assets/svg/star_24px.svg";
import StarIcon from "../../../../assets/svg/star_border_24px.svg";
import { BATTERY_ISSUE_PING_MSGS } from "../../../../shared/constants";
import { ExtraInfoFields, iDeviceDetails, iDevicePing, iExtraInfo, iFullStoreState, iPerson, ItemType } from "../../../../shared/interfaces";
import { useFirebase } from "../../../../states/firebase-state";
import { useRedux } from "../../../../states/redux-state";
import SvgIcon, { IconSize } from '../../../SvgIcon/SvgIcon';
import moment from 'moment';
import { useTbs } from '../hooks/useTbs';
import { getExtraInfoKeyByName } from '../../../../utils';
import styles from "./DeviceListItem.module.scss";

const AUCTION_EXTRA_INFO_FIELD = '@ Auction';

const DeviceListItem = ({
  deviceId,
  withIndicators = true,
  withDeviceImage = false,
  iconSize = IconSize.XS,
  subtext = '',
}) => {
  const device = useSelector<iFullStoreState, iDeviceDetails>(
    (state) => state.devicesData.devicesDetails.get(deviceId)!,
    (left, right) => equals(left, right)
  );
  const isInventoryOnlyDevice = device?.isInventoryOnlyDevice;
  const { isTbs } = useTbs(deviceId);
  const lastPing = useSelector<iFullStoreState, iDevicePing | undefined>(
    (state) => state.devicesData.devicesLastPing.get(deviceId),
    (left, right) => left?.pointId === right?.pointId
  );
  const pingMsg = lastPing?.msg ?? 'N/A';
  const hasSpeed = Boolean(lastPing?.speed);
  const uid = useRedux((s) => s.auth.user.uid);
  const [isFavorite, setFavorite] = useFirebase(
    `favorites/${uid}/${ItemType.device}/${deviceId}`
  );
  const [starIcon, setStarIcon] = useState(StarIcon);
  const [makeKey, setMakeKey] = useState<string | null>(null);
  const [modelKey, setModelKey] = useState<string | null>(null);
  const [typeKey, setTypeKey] = useState<string | null>(null);

  const [makeValue, setMakeValue] = useState<any>(null);
  const [modelValue, setModelValue] = useState<any>(null);
  const [typeValue, setTypeValue] = useState<any>(null);
  const extraInfo = device["extra-info"] || {};
  const allExtraInfo = useRedux((s) => s.general.extraInfo);

  useEffect(() => {
    if (isFavorite) setStarIcon(StarIconOrange);
  }, [isFavorite]);

  const pingMsgCheck = [
    'Ignition on',
    'Acceleration Threshold',
    'Acceleration threshold',
    'Deceleration Threshold',
    'Deceleration threshold',
    'Idle Threshold',
    'Idle threshold',
    'Ignition On Report',
    'Ignition On Interval Report',
    'Direction Change',
    'Virtual Ignition On Interval Report',
    'Virtual Ignition On Report',
    'Move Start',
    'Move Stop',
    'Trip Start',
  ].includes(pingMsg);

  const toggleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorite(isFavorite ? null : 999999);
  };

  const getExtraInfoFieldValue = (value: any): string | boolean | null => {
    if (typeof value === 'string') return value;
    if (typeof value === 'boolean') return value;
    if (value && typeof value === 'object') {
      if (typeof value.text === 'string') return value.text;
      if (typeof value.boolean === 'boolean') return value.boolean;
    }

    return null;
  };

  const findValueByKey = (obj: any, targetKey: string): string | null => {
    if (typeof obj !== 'object' || obj === null) return null;

    for (const key in obj) {
      if (key === targetKey) {
        const value = obj[key];
        if (typeof value === 'string') return value;
        if (typeof value === 'object' && value !== null) {
          if (typeof value.text === 'string') return value.text;
          return String(value);
        }
        return null;
      }

      const value = obj[key];
      if (typeof value === 'object') {
        const result = findValueByKey(value, targetKey);
        if (result !== null) return result;
      }
    }

    return null;
  }

  const auctionFieldId = useMemo(() => {
    if (!allExtraInfo) return null;

    return Object.values(allExtraInfo as Record<string, iExtraInfo>).find(
      (field) => field.details?.name?.trim() === AUCTION_EXTRA_INFO_FIELD
    )?.details?.id || null;
  }, [allExtraInfo]);

  useEffect(() => {
    const fetchKeys = async () => {
      const newMakeKey = await getExtraInfoKeyByName(ExtraInfoFields.MAKE);
      const newModelKey = await getExtraInfoKeyByName(ExtraInfoFields.MODEL);
      const newTypeKey = await getExtraInfoKeyByName(ExtraInfoFields.TYPE);

      setMakeKey(newMakeKey);
      setModelKey(newModelKey);
      setTypeKey(newTypeKey);
    };

    fetchKeys();
  }, [device]);

  useEffect(() => {
    if (makeKey) setMakeValue(findValueByKey(extraInfo, makeKey));
    if (modelKey) setModelValue(findValueByKey(extraInfo, modelKey));
    if (typeKey) setTypeValue(findValueByKey(extraInfo, typeKey));
  }, [makeKey, modelKey, typeKey, extraInfo]);

  const getExtraInfoValue = (key: ExtraInfoFields) => {
    switch (key) {
      case ExtraInfoFields.MAKE:
        return makeValue;
      case ExtraInfoFields.MODEL:
        return modelValue;
      case ExtraInfoFields.TYPE:
        return typeValue;
      default:
        return null;
    }
  };
  const make = getExtraInfoValue(ExtraInfoFields.MAKE);
  const model = getExtraInfoValue(ExtraInfoFields.MODEL);
  const type = getExtraInfoValue(ExtraInfoFields.TYPE);
  const auctionValue = auctionFieldId ? getExtraInfoFieldValue(extraInfo[auctionFieldId]) : null;
  const isAuction = typeof auctionValue === 'string'
    ? auctionValue.trim().length > 0
    : auctionValue === true;

  const assignedDriverId = device?.assignedTo?.personId;
  const assignedDriver = useSelector<iFullStoreState, iPerson>(
    (state) => (assignedDriverId ? state.general.people[assignedDriverId] : null)
  );

  const fullContent = [
    device.name,
    make && model
      ? `| ${make} ${model}${type ? ` | ${type}` : ''}`
      : make || model
        ? `| ${make || model}${type ? ` | ${type}` : ''}`
        : type
          ? `| ${type}`
          : '',
    subtext ? subtext : '',
    assignedDriver ? `Assigned: ${assignedDriver.displayName}` : ''
  ].filter(Boolean).join(' ');

  const getIndicatorDot = () => {
    if (device?.isArchived) {
      return <div className={styles.DeviceListItemIndicator} style={{ backgroundColor: 'black' }} title="Archived" />;
    }

    if (isTbs) {
      return <div className={styles.DeviceListItemIndicator} style={{ backgroundColor: '#2196F3' }} title="To Be Sold" />;
    }

    if (isInventoryOnlyDevice) {
      return <div className={styles.DeviceListItemIndicator} style={{ backgroundColor: 'orange' }} title="Not Tracked"/>;
    }


    if (lastPing?.time && moment().diff(moment(lastPing.time), 'weeks') >= 3) {
      return <div className={styles.DeviceListItemIndicator} style={{ backgroundColor: 'grey' }} title="Not Actvice 3wks+"/>;
    }

    if (pingMsg === 'Heartbeat') {
      return <div className={styles.DeviceListItemIndicator} style={{ backgroundColor: 'pink' }} />;
    }


    const ignitionOffMsgs = [
      'Ignition off',
      'Ignition Off Report',
      'Ignition off Report',
      'Ignition Off Interval Report',
      'Virtual Ignition Off Report',
      'Trip Stop',
      '',
    ];

    if (ignitionOffMsgs.includes(pingMsg)) {
      return <div className={styles.DeviceListItemIndicator} style={{ backgroundColor: 'red' }} title="Ignition OFF" />;
    }

    if (pingMsgCheck && !hasSpeed) {
      return <div className={styles.DeviceListItemIndicator} style={{ backgroundColor: 'yellow' }} title="Idle"/>;
    }

    if (pingMsgCheck && hasSpeed) {
      return <div className={styles.DeviceListItemIndicator} style={{ backgroundColor: 'green' }} title="Ignition ON"/>;
    }

    if (BATTERY_ISSUE_PING_MSGS.includes(pingMsg as typeof BATTERY_ISSUE_PING_MSGS[number])) {
      return (
        <div
          className={classNames(styles.DeviceListItemIndicator, styles.DeviceListItemIndicatorIssue)}
          title="Battery Issue"
        />
      );
    }

    return <div className={styles.DeviceListItemIndicator} style={{ backgroundColor: '' }} />;
  };


  return (
    <div className={classNames(styles.DeviceListItem, {
      [styles.isAuction]: isAuction,
      [styles.isTbs]: !!isTbs,
      [styles.isInventoryOnlyDevice]: !!isInventoryOnlyDevice,
    })}>
      <div className={styles.DeviceListItemInfoContainer}>
        <div className={styles.DeviceListItemInfo}>
          {withIndicators && getIndicatorDot()}
          <span className={styles.ContentWrapper} title={fullContent}>
            {withDeviceImage && (
              <SvgIcon size={iconSize} color="#111" icon={device.icon} />
            )}
            <b>{device.name}</b>
            {(make || model || type) && (
              <>
                {' | '}
                {[`${make || ''} ${model || ''}`.trim(), type].filter(Boolean).join(' | ')}
              </>
            )}
            {subtext && (
              <span className={styles.subtitle}>{subtext}</span>
            )}
            {device?.assignedTo && assignedDriver && (
              <span className={styles.AssignedDriver}>
                <PersonThinIcon className={styles.AssignedDriver__icon} />
                {assignedDriver?.displayName}
              </span>
            )}
          </span>
        </div>
      </div>
      <img
        className={classNames(styles.DeviceListItemFavorite, {
          [styles.DeviceListItemFavorite__favorite]: isFavorite,
        })}
        src={starIcon}
        onClick={toggleFav}
        onMouseEnter={() => setStarIcon(StarIconBlack)}
        onMouseLeave={() => {
          if (isFavorite) {
            setStarIcon(StarIconOrange);
          } else {
            setStarIcon(StarIcon);
          }
        }}
        alt="Favorite"
      />
    </div>
  );
};

export default DeviceListItem;
